import React, { useCallback, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import c from 'classnames';
import { $getSelection } from 'lexical';
import { up } from 'styled-breakpoints';
import { LggOptionsDropdownButtonWithCustomTrigger } from 'src/components/general/button/dropdown-button';
import {
  DrawerOption,
  OptionsBottomDrawer,
} from 'src/components/general/drawer/bottom/options-bottom-drawer';
import { ContactInteractionInputManagerContext } from 'src/components/pages/conversations/components/contact-interactions/contact-interactions-input-area/contact-interaction-input-manager';
import { $createTemplatePresetPlaceholderResolverNode } from 'src/components/pages/conversations/components/general/lexical-editor/nodes/template-placeholders-nodes/placeholders-resolvers/template-preset-placeholder-resolver';
import {
  BasePluginProps,
  PluginIcon,
} from 'src/components/pages/conversations/components/general/lexical-editor/plugins/shared';
import {
  ContactTextPlaceholderKey,
  getPresetPlaceholderValue,
} from 'src/components/pages/conversations/components/general/text-placeholders-helpers';
import { useBreakpoint } from 'src/hooks/use-breakpoint';
import { useVisible } from 'src/hooks/use-visible';

export const InsertPresetMessageTemplatePlaceholderPlugin = ({
  disabled = false,
}: BasePluginProps) => {
  const { t } = useTranslation(['common', 'conversations']);
  const [editor] = useLexicalComposerContext();
  const insertContactPlaceholderOverlayVisibilityHandler = useVisible();
  const breakpointUpMd = useBreakpoint(up('md'));
  const { contact } = useContext(ContactInteractionInputManagerContext);

  const insertPresetPlaceholder = useCallback(
    (presetKey: ContactTextPlaceholderKey) => {
      editor.focus();

      editor.update(() => {
        const selection = $getSelection();
        const placeholderValue = getPresetPlaceholderValue(presetKey, contact) ?? '';

        const templatePlaceholder = $createTemplatePresetPlaceholderResolverNode({
          fallbackValue: placeholderValue,
          presetKey,
          value: placeholderValue,
        });

        if (selection) {
          selection.insertNodes([templatePlaceholder]);
        }

        insertContactPlaceholderOverlayVisibilityHandler.close();
      });
    },
    [contact, editor, insertContactPlaceholderOverlayVisibilityHandler],
  );

  const pluginTriggerIcon = useMemo(() => {
    return (
      <PluginIcon
        type="placeholder"
        lggTestId="insert-preset-message-template-placeholder-plugin-trigger"
        className={c({
          active: insertContactPlaceholderOverlayVisibilityHandler.visible,
        })}
        disabled={disabled}
        onClick={() => {
          if (disabled) {
            return;
          }

          insertContactPlaceholderOverlayVisibilityHandler.setVisible(
            !insertContactPlaceholderOverlayVisibilityHandler.visible,
          );
        }}
        tooltip={t(
          'conversations:messageInput.options.messageTemplates.createPlaceholderPlugin.tooltip',
        )}
        isOpen={insertContactPlaceholderOverlayVisibilityHandler.visible}
      />
    );
  }, [disabled, insertContactPlaceholderOverlayVisibilityHandler, t]);

  const { firstName, lastName, fullName, primaryEmail, primaryPhone } = contact;

  const presetOptions: DrawerOption[] = useMemo(
    () => [
      {
        label: t('common:firstName'),
        onClick: () => insertPresetPlaceholder(ContactTextPlaceholderKey.firstName),
        'data-lgg-id': 'placeholder-option-first-name',
        disabled: !Boolean(firstName),
      },
      {
        label: t('common:lastName'),
        onClick: () => insertPresetPlaceholder(ContactTextPlaceholderKey.lastName),
        'data-lgg-id': 'placeholder-option-last-name',
        disabled: !Boolean(lastName),
      },
      {
        label: t('common:phoneNumber'),
        onClick: () => insertPresetPlaceholder(ContactTextPlaceholderKey.phoneNumber),
        'data-lgg-id': 'placeholder-option-phone-number',
        disabled: !Boolean(primaryPhone?.national),
      },
      {
        label: t('common:email'),
        onClick: () => insertPresetPlaceholder(ContactTextPlaceholderKey.email),
        'data-lgg-id': 'placeholder-option-email',
        disabled: !Boolean(primaryEmail),
      },
      {
        label: 'Full name',
        onClick: () => insertPresetPlaceholder(ContactTextPlaceholderKey.fullName),
        'data-lgg-id': 'placeholder-option-full-name',
        disabled: !Boolean(fullName),
      },
    ],
    [
      firstName,
      fullName,
      insertPresetPlaceholder,
      lastName,
      primaryEmail,
      primaryPhone?.national,
      t,
    ],
  );

  if (disabled) {
    return pluginTriggerIcon;
  }

  return (
    <div>
      {breakpointUpMd ? (
        <LggOptionsDropdownButtonWithCustomTrigger
          customTrigger={pluginTriggerIcon}
          customDropdownProps={{
            placement: 'topLeft',
            overlayStyle: {
              height: '147px',
              width: '194px',
            },
          }}
          options={presetOptions}
          visibilityHandler={insertContactPlaceholderOverlayVisibilityHandler}
        />
      ) : (
        <>
          {pluginTriggerIcon}
          <OptionsBottomDrawer
            visible={insertContactPlaceholderOverlayVisibilityHandler.visible}
            title={t(
              'conversations:messageInput.options.messageTemplates.createPlaceholderPlugin.insertPlaceholder',
            )}
            onClose={insertContactPlaceholderOverlayVisibilityHandler.close}
            options={presetOptions}
          />
        </>
      )}
    </div>
  );
};
